/* eslint-disable max-len,no-unused-vars,arrow-body-style,no-restricted-syntax */
import { debounce, throttle, uniqBy, cloneDeep } from 'lodash';
import constants from '@/constants';
import ls from '@/helpers/localStorage';
import sessionHistory, { SESSION_HISTORY_KEY } from '@/helpers/sessionHistory';
import log from '@/logger';
import api from '@/services/api';
import { MARKET_DATA_MAP, CONTRACT_DATA_MAP } from '@/services/notifications/proxy';
import { updateContract, updateMarket, updateMarketContracts } from '@/utils/notifications';

const PIN_KEY_DISCUSSION = 'discussion-pin-key';
const PIN_KEY_SESSION_HISTORY = 'session-history-pin-key';
const PIN_KEY_WATCHLIST = 'watchlist-pin-key';
const PIN_KEY_MARKET_HISTORY = 'market-history-pin-key';
const PIN_KEY_OPENED = 'pin-opened-key';

const MAX_RELATED_MARKETS = 3;
const MARKET_DEBOUNCE_INTERVAL = 750;
const debounceGetMarket = debounce((dispatch, marketId) => {
  dispatch('getMarket', { marketId, skipCache: true });
}, MARKET_DEBOUNCE_INTERVAL);

const ITEMS_PER_HISTORY_PAGE = 10;
const DEFAULT_HISTORY = {
  data: null,
  marketId: -1,
  totalCount: -1,
  canLoadMore: true,
};
const DEFAULT_MARKET_SUMMARY_DATA = {
  data: [],
  page: 0,
  totalCount: 0,
  canLoadMore: true,
};
const createDefaultTopComments = () => ({
  title: 'Top Recent Comments',
  comments: [],
});
const SUMMARY_DATA_THROTTLE_TIME = 500;

export default {
  namespaced: true,
  state: {
    marketIsEngineBusy: false,
    marketIsEngineBusyMessage: null,
    marketLoadError: false,
    market: null,
    contracts: [],
    contract: null,
    history: DEFAULT_HISTORY,
    marketSummaryData: Object.create(DEFAULT_MARKET_SUMMARY_DATA),
    marketSummaryDataDate: '',
    marketSummaryLimitExceededMessage: '',
    isRulesOpen: false,
    related: [],
    chartTimeline: null,
    chartType: null,
    marketContractsStats: [],
    mapStateData: {},
    mapStateDataSenate: {},
    mapOverviewData: [],
    mapOverviewDataSenate: [],
    marketMapTooltipVisible: false,
    isDiscussionPinned: ls.get(PIN_KEY_DISCUSSION, false),
    isWatchlistPinned: ls.get(PIN_KEY_WATCHLIST, false),
    isSessionHistoryPinned: ls.get(PIN_KEY_SESSION_HISTORY, false),
    isMarketHistoryPinned: ls.get(PIN_KEY_MARKET_HISTORY, false),
    pinnedOpened: ls.get(PIN_KEY_OPENED, []),
    relatedContracts: [],
    relatedProducts: {
      active: [],
      closed: [],
    },
    marketTopComments: createDefaultTopComments(),
    marketTopCommentsMarketId: null,
    marketTopCommentsLoaded: false,
    marketTopCommentsLoading: false,
    reloadHistoryToggle: 0,
    sessionHistory: ls.get(SESSION_HISTORY_KEY, []),
    activeMarketTab: null,
  },
  getters: {
    historyData: (state) => {
      const historyData = state.history.data;
      return historyData === null ? [] : historyData;
    },
  },
  mutations: {
    setMarket(state, payload) {
      state.market = payload;
    },
    setContract(state, payload) {
      state.contract = payload;
    },
    setContracts(state, payload) {
      state.contracts = payload;
    },
    setMarketLoadError(state, payload) {
      state.marketLoadError = payload;
    },
    setMarketEngineBusyMessage(state, { isEngineBusy, isEngineBusyMessage }) {
      state.marketIsEngineBusy = isEngineBusy;
      state.marketIsEngineBusyMessage = isEngineBusyMessage;
    },
    updateMarketFromNotification(state, data) {
      state.market = updateMarket(state.market, data);
    },
    updateContractFromNotification(state, data) {
      if (state.market !== null && typeof state.market.contracts !== 'undefined') {
        // CONTRACTS SORT
        // contracts.sort((prev, next) => {
        //   if (prev.lastTradePrice < next.lastTradePrice) {
        //     return 1;
        //   } else if (prev.lastTradePrice > next.lastTradePrice) {
        //     return -1;
        //   }
        //   return 0;
        // });
        state.market = updateMarketContracts(state.market, data);
      }
      if (state.contracts !== null) {
        // CONTRACTS SORT
        // state.contracts.sort((prev, next) => {
        //   if (prev.lastTradePrice < next.lastTradePrice) {
        //     return 1;
        //   } else if (prev.lastTradePrice > next.lastTradePrice) {
        //     return -1;
        //   }
        //   return 0;
        // });
        const contracts = state.contracts.slice();
        state.contracts = contracts.map((contract) => updateContract(contract, data));
      }
      if (state.contract !== null) {
        state.contract = updateContract(state.contract, data);
      }
    },
    updateMapOverviewDataFromNotification(state, data) {
      if (state.mapOverviewData.length > 0) {
        const mapOverviewData = state.mapOverviewData.slice();
        state.mapOverviewData = mapOverviewData.map((contract) => updateContract(contract, data));
      }

      const mapStateData = cloneDeep(state.mapStateData);

      for (const stateCode of Object.keys(mapStateData)) {
        mapStateData[stateCode] = updateMarketContracts(mapStateData[stateCode], data);
      }

      state.mapStateData = mapStateData;
    },
    updateMapOverviewDataSenateFromNotification(state, data) {
      if (state.mapOverviewDataSenate.length > 0) {
        const mapOverviewData = state.mapOverviewDataSenate.slice();
        state.mapOverviewDataSenate = mapOverviewData.map((contract) =>
          updateContract(contract, data),
        );
      }

      const mapStateData = cloneDeep(state.mapStateDataSenate);

      for (const stateCode of Object.keys(mapStateData)) {
        mapStateData[stateCode] = updateMarketContracts(mapStateData[stateCode], data);
      }

      state.mapStateDataSenate = mapStateData;
    },
    resetHistory(state) {
      state.history = DEFAULT_HISTORY;
    },
    updateHistory(state, payload) {
      state.history = {
        data: payload.data.historyList,
        marketId: payload.marketId,
        totalCount: payload.data.totalCount,
        canLoadMore: payload.data.totalCount > payload.data.historyList.length,
      };
    },
    setMarketSummaryLimitExceeded(state, message) {
      state.marketSummaryLimitExceededMessage = message;
    },
    updateMarketSummaryData(state, payload) {
      const data = uniqBy(
        state.marketSummaryData.data.concat(payload.SummaryContractData || []),
        (i) => i.MarketSummaryDataId,
      );
      const page = payload.Page;
      const totalCount = payload.TotalItems;
      const canLoadMore = totalCount > data.length;

      state.marketSummaryData = {
        ...state.marketSummaryData,
        data,
        page,
        totalCount,
        canLoadMore,
      };
    },
    resetMarketSummaryData(state) {
      state.marketSummaryData = Object.create(DEFAULT_MARKET_SUMMARY_DATA);
      state.marketSummaryLimitExceededMessage = '';
    },
    setOpenRules(state, payload) {
      state.isRulesOpen = payload;
    },
    setRelatedMarkets(state, payload) {
      state.related = payload;
    },
    clearRelatedMarkets(state) {
      state.related = [];
    },
    updateRelatedMarketFromNotification(state, data) {
      if (state.related.length > 0) {
        state.related = state.related.map((market) => updateMarket(market, data));
      }
    },
    updateRelatedContractFromNotification(state, data) {
      if (state.related.length > 0) {
        state.related = state.related.map((market) => updateMarketContracts(market, data));
      }
    },
    setChartTimeline(state, data) {
      state.chartTimeline = data;
    },
    setChartType(state, data) {
      state.chartType = data;
    },
    setMarketContractsStats(state, data) {
      state.marketContractsStats = data;
    },
    clearMarketContractsStats(state) {
      state.marketContractsStats = [];
    },
    setMapOverviewData(state, data) {
      state.mapOverviewData = data;
    },
    setMapOverviewDataSenate(state, data) {
      state.mapOverviewDataSenate = data;
    },
    setMapStateData(state, data) {
      state.mapStateData = data;
    },
    setMapStateDataSenate(state, data) {
      state.mapStateDataSenate = data;
    },
    setMarketMapTooltipVisible(state, flag) {
      state.marketMapTooltipVisible = flag;
    },
    setMarketSummaryDataDate(state, value) {
      state.marketSummaryDataDate = value;
    },
    toggleDiscussionPin(state) {
      if (state.isDiscussionPinned) {
        state.isDiscussionPinned = false;
        ls.set(PIN_KEY_DISCUSSION, false);
      } else {
        state.isDiscussionPinned = true;
        ls.set(PIN_KEY_DISCUSSION, true);
      }
    },
    toggleWatchlistPin(state) {
      if (state.isWatchlistPinned) {
        state.isWatchlistPinned = false;
        ls.set(PIN_KEY_WATCHLIST, false);
      } else {
        state.isWatchlistPinned = true;
        ls.set(PIN_KEY_WATCHLIST, true);
      }
    },
    toggleSessionHistoryPin(state) {
      if (state.isSessionHistoryPinned) {
        state.isSessionHistoryPinned = false;
        ls.set(PIN_KEY_SESSION_HISTORY, false);
      } else {
        state.isSessionHistoryPinned = true;
        ls.set(PIN_KEY_SESSION_HISTORY, true);
      }
    },
    toggleMarketHistoryPin(state) {
      if (state.isMarketHistoryPinned) {
        state.isMarketHistoryPinned = false;
        ls.set(PIN_KEY_MARKET_HISTORY, false);
      } else {
        state.isMarketHistoryPinned = true;
        ls.set(PIN_KEY_MARKET_HISTORY, true);
      }
    },
    clearRelatedContracts(state) {
      state.relatedContracts = [];
    },
    setRelatedContracts(state, data) {
      state.relatedContracts = data;
    },
    setRelatedProducts(state, data) {
      state.relatedProducts = {
        active: data.activeMarkets || [],
        closed: data.closedMarkets || [],
      };
    },
    setMarketTopComments(state, { marketId, data }) {
      state.marketTopCommentsMarketId = marketId;
      state.marketTopCommentsLoaded = true;
      state.marketTopCommentsLoading = false;
      state.marketTopComments = data || createDefaultTopComments();
    },
    setMarketTopCommentsLoading(state, payload) {
      state.marketTopCommentsLoading = payload;
    },
    clearMarketTopComments(state) {
      state.marketTopComments = createDefaultTopComments();
      state.marketTopCommentsMarketId = null;
      state.marketTopCommentsLoaded = false;
      state.marketTopCommentsLoading = false;
    },
    incrReloadHistoryToggle(state) {
      state.reloadHistoryToggle += 1;
    },
    toggleOpenPinSection(state, name) {
      const opened = [...state.pinnedOpened];
      if (opened.includes(name)) {
        state.pinnedOpened = opened.filter((i) => i !== name);
      } else {
        state.pinnedOpened = [...opened, name];
      }
    },
    pushSessionHistoryContract(state, data) {
      const marketId = state.market && state.market.marketId;
      state.sessionHistory = sessionHistory.pushContract({
        value: { marketId, ...data.value },
      });
    },
    setActiveMarketTab(state, tab) {
      state.activeMarketTab = tab;
    },
  },
  actions: {
    setActiveMarketTab({ commit }, tab) {
      commit('setActiveMarketTab', tab);
    },
    updateHeaderHeightclearMarket({ commit }) {
      commit('setMarket', {});
      commit('setContracts', []);
    },
    clearMarketEngineBusyMessage({ commit }) {
      commit('setMarketEngineBusyMessage', {
        isEngineBusy: false,
        isEngineBusyMessage: null,
      });
    },
    getMarket(
      { commit, dispatch, state },
      { marketId, commentThreadId = '', skipCache = false, modifyState = true },
    ) {
      commit('setMarketLoadError', false);
      if (!skipCache && state.market !== null && state.market.marketId === marketId) {
        return Promise.resolve(state.market);
      }
      let marketData = {};
      return api.market
        .getMarket(marketId, commentThreadId)
        .then((response) => {
          marketData = response.data;
        })
        .then(() => api.market.getMarketContracts(marketId))
        .then((response) => {
          marketData.contracts = response.data;
          if (modifyState) {
            commit('setContracts', marketData.contracts);
            commit('setMarket', marketData);
          }
          return marketData;
        })
        .catch((error) => {
          if (modifyState) {
            commit('setMarketLoadError', true);
            dispatch('clearMarket');
          }
          throw error;
        });
    },
    getMarketContracts({ commit, state }, marketId) {
      return api.market.getMarketContracts(marketId).then((response) => {
        commit('setContracts', response.data);
        commit('setMarket', {
          ...state.market,
          contracts: response.data,
        });
        return response.data;
      });
    },
    getMarketRules({ commit }, marketId) {
      return api.market.getMarketRules(marketId).then((response) => response.data);
    },
    getContract({ commit, state }, contractId) {
      if (state.contract !== null && state.contract.contractId === contractId) {
        return Promise.resolve(state.contract);
      }
      if (state.contracts !== null) {
        const contract = state.contracts.find((c) => c.contractId === contractId);
        if (typeof contract !== 'undefined') {
          commit('setContract', contract);
          return Promise.resolve(contract);
        }
      }
      return Promise.resolve();
    },
    getMarketContractsStats({ commit, dispatch }, marketId) {
      return api.market
        .getMarketContractsStats(marketId)
        .then((response) => {
          commit('setMarketContractsStats', response.data);
          return response.data;
        })
        .catch((error) => {
          log.error('Failed to load market contracts stats.', error);
          dispatch('clearMarketContractsStats');
        });
    },
    clearMarketContractsStats({ commit }) {
      commit('clearMarketContractsStats');
    },
    resetHistory({ commit }) {
      commit('resetHistory');
    },
    getMarketHistory({ state, commit }, { marketId, reload = false }) {
      const history = state.history;
      if (history !== null && history.marketId === marketId && !reload) {
        return history.data;
      }
      // on new market but in history old data
      commit('resetHistory');
      return api.profile.getTradeHistory(ITEMS_PER_HISTORY_PAGE, 1, marketId).then((response) => {
        commit('updateHistory', {
          data: response.data,
          marketId,
        });
        return response.data;
      });
    },
    getRelatedMarkets({ commit }, marketId) {
      return api.market.getMarketRelated(marketId).then((response) => {
        const data = response.data.slice(0, MAX_RELATED_MARKETS);
        commit('setRelatedMarkets', data);
        return data;
      });
    },
    clearRelatedMarkets({ commit }) {
      commit('clearRelatedMarkets');
    },
    updateMarketFromNotification({ commit, dispatch, state }, payload) {
      const marketId = payload.MarketId;
      if (state.market !== null && state.market.marketId === marketId) {
        commit('updateMarketFromNotification', payload);
        // debounceGetMarket(dispatch, marketId);
      }
    },
    setMarketEngineBusyMessage({ commit }, payload) {
      commit('setMarketEngineBusyMessage', payload);
    },
    updateRelatedMarketFromNotification({ commit }, payload) {
      commit('updateRelatedMarketFromNotification', payload);
    },
    updateRelatedContractFromNotification({ commit }, payload) {
      commit('updateRelatedContractFromNotification', payload);
    },
    updateContractFromNotification({ commit }, payload) {
      commit('updateContractFromNotification', payload);
    },
    updateMapOverviewDataFromNotification({ commit }, payload) {
      commit('updateMapOverviewDataFromNotification', payload);
    },
    updateMapOverviewDataSenateFromNotification({ commit }, payload) {
      commit('updateMapOverviewDataSenateFromNotification', payload);
    },
    openRules({ commit }, payload = true) {
      commit('setOpenRules', payload);
    },
    setMarket({ commit }, market) {
      commit('setMarket', market);
    },
    setContracts({ commit }, contracts) {
      commit('setContracts', contracts);
    },
    setMarketLoadError({ commit }, flag) {
      commit('setMarketLoadError', flag);
    },
    setChartTimeline({ commit }, payload) {
      commit('setChartTimeline', payload);
    },
    setChartType({ commit }, payload) {
      commit('setChartType', payload);
    },
    getMapOverviewData({ commit }) {
      return api.map.getContracts().then((response) => {
        commit('setMapOverviewData', response.data);
        return response.data;
      });
    },
    getMapStateData({ commit }) {
      return api.map.getStateData().then((response) => {
        const data = (response.data || []).reduce((obj, item) => {
          const stateCode = item.marketName.split(' ')[4];
          const stateName = constants.statesCodeToNameMap[stateCode];
          const stateVotes = constants.statesCodeToElectoralVotesMap[stateCode];
          obj[stateCode] = {
            ...item,
            stateVotes,
            stateCode,
            stateName,
          };
          return obj;
        }, {});
        commit('setMapStateData', data);
        return data;
      });
    },
    getMapOverviewDataSenate({ commit }) {
      return api.map.getContractsSenate().then((response) => {
        commit('setMapOverviewDataSenate', response.data);
        return response.data;
      });
    },
    getMapStateDataSenate({ commit }) {
      return api.map.getStateDataSenate().then((response) => {
        const data = (response.data || []).reduce((obj, item) => {
          const stateCode = item.marketName.match(/\b[A-Z]{2}\b/)[0];
          const stateName = constants.statesCodeToNameMap[stateCode];
          const stateVotes = constants.statesCodeToElectoralVotesMap[stateCode];
          const result = {
            ...item,
            stateVotes,
            stateCode,
            stateName,
          };
          if (stateCode === 'GA') {
            if (item.marketId === 6651) {
              obj[stateCode] = result;
            } else {
              obj.GA2 = result;
            }
          }
          obj[stateCode] = result;
          return obj;
        }, {});
        commit('setMapStateDataSenate', data);
        return data;
      });
    },
    setMarketMapTooltipVisible({ commit }, flag) {
      commit('setMarketMapTooltipVisible', flag);
    },
    getCommentThread({ commit }, { marketId, threadId }) {
      return api.market.getCommentThread({ marketId, threadId }).then((response) => response.data);
    },
    getMarketTopComments({ commit }, marketId) {
      if (!marketId) {
        commit('clearMarketTopComments');
        return Promise.resolve(createDefaultTopComments());
      }

      commit('setMarketTopCommentsLoading', true);

      return api.market
        .getMarketTopComments(marketId)
        .then((response) => {
          const data = response.data || createDefaultTopComments();
          commit('setMarketTopComments', {
            marketId,
            data,
          });
          return data;
        })
        .catch((error) => {
          log.error('Failed to load market top comments.', error);
          commit('clearMarketTopComments');
          return createDefaultTopComments();
        });
    },
    getMarketSummaryData: throttle(({ state, commit }, { reset = false } = {}) => {
      const page = reset ? 1 : state.marketSummaryData.page + 1;
      const canLoadMore = reset ? true : state.marketSummaryData.canLoadMore;
      if (canLoadMore) {
        return api.browse
          .getMarketSummaryData(page, state.marketSummaryDataDate)
          .then((response) => {
            const data = response.data;
            if (reset) commit('resetMarketSummaryData');
            commit('setMarketSummaryLimitExceeded', '');
            commit('updateMarketSummaryData', Object.assign(data, { page }));
            return data;
          })
          .catch((error) => {
            const response = error.response;
            if (response && response.data) {
              const data = Array.isArray(response.data) ? response.data[0] : response.data;
              const showLimitExceeded = data.code && data.code === 'LIMIT_EXCEEDED';
              const message = showLimitExceeded ? data.message : '';
              commit('setMarketSummaryLimitExceeded', message);
            }
          });
      }
      return Promise.resolve(1);
    }, SUMMARY_DATA_THROTTLE_TIME),
    resetMarketSummaryData({ commit }) {
      commit('resetMarketSummaryData');
    },
    clearMarketTopComments({ commit }) {
      commit('clearMarketTopComments');
    },
    setMarketSummaryDataDate({ commit }, value) {
      commit('setMarketSummaryDataDate', value);
    },
    toggleDiscussionPin({ commit }) {
      commit('toggleDiscussionPin');
    },
    toggleWatchlistPin({ commit }) {
      commit('toggleWatchlistPin');
    },
    toggleSessionHistoryPin({ commit }) {
      commit('toggleSessionHistoryPin');
    },
    toggleMarketHistoryPin({ commit }) {
      commit('toggleMarketHistoryPin');
    },
    clearRelatedContracts({ commit }) {
      commit('clearRelatedContracts');
    },
    getRelatedContracts({ commit }, contractId) {
      return api.market.getRelatedContracts(contractId).then((response) => {
        const data = response.data;
        commit('setRelatedContracts', data);
        return data;
      });
    },
    getRelatedProducts({ commit }, marketId) {
      return api.market.getMarketRelatedProducts(marketId).then((response) => {
        const data = response.data;
        commit('setRelatedProducts', data);
        return data;
      });
    },
    incrReloadHistoryToggle({ commit }) {
      commit('incrReloadHistoryToggle');
    },
    toggleOpenPinSection({ commit }, name) {
      commit('toggleOpenPinSection', name);
    },
    pushSessionHistoryContract({ commit }, data) {
      commit('pushSessionHistoryContract', data);
    },
    clearMarket({ commit }) {
      commit('setMarket', {});
      commit('setContracts', []);
      commit('clearMarketTopComments');
    },
  },
};
